<template>
    <tailwind-layout title="Email Logs">
        <div class="space-y-6">
            <BridgeBackLink />
            <header>
                <h1 class="text-2xl font-bold tracking-tight text-rent-risk-blue-400">Email Logs</h1>
                <p class="mt-1 text-sm text-gray-600">View all sent emails from the system</p>
            </header>
            <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-3 sm:grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch ml-2 lg:ml-0">
                    <div class="sm:w-2/12 pr-4 order-2 sm:order-1">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="col-span-2 sm:w-3/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <select v-model="form.status" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md">
                            <option value="">All Status</option>
                            <option value="sent">Sent</option>
                            <option value="failed">Failed</option>
                            <option value="pending">Pending</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-3/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <select v-model="form.action_type" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md">
                            <option value="">All Types</option>
                            <option value="automatic">Automatic</option>
                            <option value="user_triggered">User Triggered</option>
                        </select>
                    </div>
                    <div class="col-span-3 sm:w-4/12 sm:pr-4 order-1 sm:order-2 px-2">
                        <input id="search" v-model="form.search" type="text" placeholder="Search by subject, email, or mail class..." class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <table class="min-w-full divide-y divide-gray-300 hidden lg:table">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider">
                                            Subject
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-left text-xs font-bold uppercase tracking-wider min-w-[200px]">
                                            Recipients
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">
                                            Mail Class
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">
                                            Type
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th scope="col" class="px-6 py-3 text-center text-xs font-bold uppercase tracking-wider">
                                            Sent At
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(log, index) in emailLogs.data" :key="index" 
                                        class="bg-white hover:bg-gray-100 cursor-pointer"
                                        @click="router.visit(route('email-logs.show', log.id))">
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 max-w-xs truncate" :title="log.subject">
                                            {{ log.subject }}
                                        </td>
                                        <td class="px-6 py-4 text-sm text-gray-500 max-w-md">
                                            <div v-if="Array.isArray(log.recipients) && log.recipients.length > 0" 
                                                 class="truncate"
                                                 :title="log.recipients.map(r => r.email || r).join(', ')">
                                                <span class="inline-block truncate">
                                                    {{ log.recipients[0].email || log.recipients[0] }}
                                                </span>
                                                <span v-if="log.recipients.length > 1" class="text-gray-400 ml-1">
                                                    +{{ log.recipients.length - 1 }} more
                                                </span>
                                            </div>
                                            <span v-else class="text-gray-400">N/A</span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center max-w-xs truncate" :title="log.mail_class">
                                            {{ log.mail_class.split('\\').pop() }}
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                                                  :class="log.action_type === 'automatic' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'">
                                                {{ log.action_type === 'automatic' ? 'Automatic' : 'User Triggered' }}
                                            </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-center">
                                            <span
                                                :class="[
                                                    'inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold',
                                                    log.status === 'sent' ? 'bg-green-100 text-green-700' :
                                                    log.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                    'bg-yellow-100 text-yellow-700'
                                                ]"
                                            >
                                                {{ log.status }}
                                            </span>
                                        </td>
                                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-center">
                                            {{ log.sent_at || 'Not sent' }}
                                        </td>
                                    </tr>
                                    <tr v-show="!emailLogs.data.length">
                                        <td colspan="6" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No email logs found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                            <!-- Mobile view -->
                            <div class="lg:hidden space-y-4 p-4">
                                <div v-for="(log, index) in emailLogs.data" 
                                     :key="index" 
                                     class="bg-white border rounded-lg p-4 shadow cursor-pointer hover:shadow-md transition-shadow"
                                     @click="router.visit(route('email-logs.show', log.id))">
                                    <div class="flex justify-between items-start mb-2">
                                        <h3 class="font-semibold text-sm text-gray-900 truncate flex-1" :title="log.subject">{{ log.subject }}</h3>
                                        <span
                                            :class="[
                                                'inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold ml-2',
                                                log.status === 'sent' ? 'bg-green-100 text-green-700' :
                                                log.status === 'failed' ? 'bg-red-100 text-red-700' :
                                                'bg-yellow-100 text-yellow-700'
                                            ]"
                                        >
                                            {{ log.status }}
                                        </span>
                                    </div>
                                    <div class="text-xs text-gray-600 space-y-1">
                                        <div><strong>Recipients:</strong> 
                                            <div v-if="Array.isArray(log.recipients) && log.recipients.length > 0" class="mt-1">
                                                <div v-for="(recipient, idx) in log.recipients" :key="idx" class="truncate">
                                                    {{ recipient.email || recipient }}
                                                </div>
                                            </div>
                                            <span v-else>N/A</span>
                                        </div>
                                        <div><strong>Type:</strong> 
                                            <span :class="log.action_type === 'automatic' ? 'text-blue-600' : 'text-purple-600'">
                                                {{ log.action_type === 'automatic' ? 'Automatic' : 'User Triggered' }}
                                            </span>
                                        </div>
                                        <div><strong>Sent:</strong> {{ log.sent_at || 'Not sent' }}</div>
                                    </div>
                                    <div class="mt-3 text-rent-risk-blue-600 text-sm font-medium">
                                        View Details →
                                    </div>
                                </div>
                                <div v-if="!emailLogs.data.length" class="text-center text-gray-500 py-8">
                                    No email logs found.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="emailLogs.links" :results="emailLogs" :type="'emailLogs'" />
        </div>
        </div>
    </tailwind-layout>
</template>

<script setup>
import { ref, watch } from 'vue'
import { router } from '@inertiajs/vue3'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import Pagination from '@/Shared/Pagination.vue'
import BridgeBackLink from '@/Shared/BridgeBackLink.vue'

const props = defineProps({
    filters: Object,
    emailLogs: Object,
    mailClasses: Array,
})

const form = ref({
    search: props.filters?.search || '',
    status: props.filters?.status || '',
    action_type: props.filters?.action_type || '',
    mail_class: props.filters?.mail_class || '',
    paginate: props.filters?.paginate || 25,
})

const handleFilterChange = throttle(() => {
    router.get(route('email-logs.index'), pickBy(form.value), { preserveState: true })
}, 150)

watch(() => form.value, handleFilterChange, { deep: true })
</script>

